import React, { FC, useCallback } from 'react';
import debounce from 'debounce-promise';
import memoize from 'memoizee';

import { MidotUserDTO, useGetMidotUsersHook, useGetUsersByIdsHook, User } from '@/api/generated';
import DropdownSelect, { DropdownSelectOption, DropdownSelectProps } from '@/components/fields/DropdownSelect';
import { UserAvatar } from '@/components/UserAvatar';
import { getUserFullName } from '@/utils/user';
import MidotUserEmailDropdownSelect, {
  type MidotUserEmailDropdownSelectProps
} from '@/views/fields/UserDropdownSelect/MidotUserEmailDropdownSelect';

const AVATAR_SIZE = 20;
const DEBOUNCE_DELAY = 300;

type MidotUserDropdownSelectProps = Omit<DropdownSelectProps, 'options'> & {
  officeSlug?: string[];
  roleId?: string;
};

const filterBySearch = (options: DropdownSelectOption[], inputValue?: string) => {
  if (!inputValue) return options;

  return options.filter(option => (option.label || '').toLowerCase().includes(inputValue?.toLowerCase() || ''));
};

const userToOption = (user: User) => ({
  label: getUserFullName(user),
  value: user.id!,
  Icon: <UserAvatar user={user} size={AVATAR_SIZE} sx={{ ml: 0.5, mr: 0.5 }} />
});

const MidotUserDropdownSelect: FC<MidotUserDropdownSelectProps> = props => {
  const getMidotUsers = useGetMidotUsersHook();
  const getUsersByIds = useGetUsersByIdsHook();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptions = useCallback(
    memoize(
      async (inputValue?: string) => {
        const usersData = await getMidotUsers({
          query: inputValue,
          office_slug: props.officeSlug,
          role_id: props.roleId
        });

        const newUserOptions = (usersData?.users || []).map((midotUser: MidotUserDTO) =>
          userToOption(midotUser as User)
        );

        return filterBySearch(newUserOptions, inputValue);
      },
      { promise: true }
    ),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptionsDebounced = useCallback(debounce(loadUserOptions, DEBOUNCE_DELAY), [loadUserOptions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptionsByIds = useCallback(
    memoize(
      async (ids?: string[]) => {
        const users = await getUsersByIds({ id: ids! });
        const newUserOptions = (users || []).map((user: User) => userToOption(user));

        return newUserOptions;
      },
      { promise: true }
    ),
    []
  );

  return (
    <DropdownSelect {...props} loadOptions={loadUserOptionsDebounced} loadSelectedOptions={loadUserOptionsByIds} />
  );
};

export type GetMidotUserDropdownSelectBoundProps = Pick<MidotUserDropdownSelectProps, 'officeSlug' | 'roleId'> & {
  disabledOptions?: string[];
};

// eslint-disable-next-line react-refresh/only-export-components
export const getMidotUserDropdownSelect = (
  bound: GetMidotUserDropdownSelectBoundProps
): React.FC<MidotUserEmailDropdownSelectProps> => {
  const BoundMidotUserDropdownSelect: React.FC<MidotUserEmailDropdownSelectProps> = props => (
    <MidotUserEmailDropdownSelect {...bound} {...props} />
  );

  return BoundMidotUserDropdownSelect;
};

export default MidotUserDropdownSelect;
